import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';

// --- helpers ---
function parseToDate(v) {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;

  const s = String(v).trim();

  // yyyy-MM-dd
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [, yyyy, mm, dd] = m.map(Number);
    return new Date(yyyy, mm - 1, dd);
  }
  // dd/MM/yyyy
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const [, dd, mm, yyyy] = m.map(Number);
    return new Date(yyyy, mm - 1, dd);
  }
  // Fallback (e.g., "Mon Sep 29 2025 00:00:00 GMT+0100...")
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toYMD(v) {
  const d = parseToDate(v);
  if (!d) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromYMD(ymd) {
  if (!ymd) return null;
  const [yyyy, mm, dd] = ymd.split('-').map(Number);
  return new Date(yyyy, (mm || 1) - 1, dd || 1);
}

// --- outer component that forces remount on prop change ---
export default function DatePicker({ label, date, onDateChange }) {
  const normalized = useMemo(() => toYMD(date), [date]);
  const remountKey = `dp-${normalized || 'empty'}`;

  return (
    <DatePickerInner
      key={remountKey}            // <-- forces full remount when prop date changes
      label={label}
      initialYMD={normalized}
      onDateChange={onDateChange}
    />
  );
}

// --- inner, stateful instance ---
function DatePickerInner({ label, initialYMD, onDateChange }) {
  const [value, setValue] = useState(initialYMD || '');

  const handleChange = (e) => {
    const ymd = e.target.value;          // "yyyy-MM-dd"
    setValue(ymd);
    const asDate = e.target.valueAsDate ?? fromYMD(ymd);
    onDateChange(asDate);              // pass back a Date object (original contract)
  };

  return (
    <TextField
      label={label}
      type="date"
      value={value}
      onChange={handleChange}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />
  );
}
